// @core/stores/affiliate/AffiliatePayoutStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  ApprovePayoutRequest,
  Payout,
  PayoutResponse,
  ProcessPayoutRequest,
  RejectPayoutRequest,
  RequestPayoutRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliatePayoutStore {
  // List state
  items: Payout[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Payout | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Payout>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Payout | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Payout | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Approve payout
   */
  async ApprovePayout(payout_id: string, data: ApprovePayoutRequest): Promise<PayoutResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ApprovePayout(payout_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get payout by ID
   */
  async GetPayout(payout_id: string): Promise<Payout> {
    // Check cache first
    const cached = this.cache.get(payout_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetPayout(payout_id);
      const item = response.payout;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List payouts
   */
  async ListPayouts(params?: PaginatedRequest & { affiliate_id?: string | number | boolean; period_end?: string | number | boolean; period_start?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListPayouts(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Process payout via BillingService
   */
  async ProcessPayout(payout_id: string, data: ProcessPayoutRequest): Promise<PayoutResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ProcessPayout(payout_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reject payout
   */
  async RejectPayout(payout_id: string, data: RejectPayoutRequest): Promise<PayoutResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.RejectPayout(payout_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request payout
   */
  async RequestPayout(data: RequestPayoutRequest): Promise<PayoutResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.RequestPayout(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const affiliatePayoutStore = new AffiliatePayoutStore();
